import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, Play, ClipboardList, BookOpen, Globe, Download, RefreshCw, Lock, Inbox } from "lucide-react";
import { toast } from "sonner";
import { Deck, LANG_FLAGS } from "@/types/linguadeck";

type StudyModeKey = "cards" | "learn" | "test" | "match";
type TimeGroup = "today" | "this_week" | "older";

function mergeUniqueDecks(existing: Deck[], incoming: Deck[]): Deck[] {
  if (incoming.length === 0) return existing;
  const merged = [...existing];
  const indexById = new Map(existing.map((deck, index) => [deck.id, index]));

  for (const deck of incoming) {
    const existingIndex = indexById.get(deck.id);
    if (existingIndex === undefined) {
      indexById.set(deck.id, merged.length);
      merged.push(deck);
      continue;
    }
    merged[existingIndex] = deck;
  }

  return merged;
}

function getTimeGroup(iso: string | undefined): TimeGroup {
  if (!iso) return "older";
  const date = new Date(iso);
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const diffDays = (todayStart - dateStart) / (24 * 60 * 60 * 1000);
  if (diffDays === 0) return "today";
  if (diffDays <= 7) return "this_week";
  return "older";
}

/** Resolve studyModes with legacy fallback: undefined → cards: true (so older decks don't vanish) */
function getResolvedStudyModes(deck: { studyModes?: Record<string, unknown>; study_modes?: Record<string, unknown> }): Record<StudyModeKey, boolean> {
  const raw = deck.studyModes ?? (deck as { study_modes?: unknown }).study_modes;
  // Legacy fallback: if studyModes missing, treat as cards-enabled (show in all tabs to avoid vanishing)
  if (!raw || typeof raw !== "object") {
    return { cards: true, learn: true, test: true, match: true };
  }
  const modes = raw as Record<string, unknown>;
  const bool = (v: unknown): boolean => v === true || v === "true";
  const def: Record<StudyModeKey, boolean> = { cards: true, learn: true, test: true, match: true };
  const keys: StudyModeKey[] = ["cards", "learn", "test", "match"];
  for (const k of keys) {
    if (modes[k] !== undefined && modes[k] !== null) {
      def[k] = bool(modes[k]);
    }
  }
  return def;
}
import { triggerBackgroundTtsForCards } from "@/lib/triggerBackgroundTts";
import {
  fetchTeacherLibraryPage,
  createDeck,
  DeckAlreadyImportedError,
  fetchGlobalDeckForStudy,
  fetchDecksLight,
  smartSyncLibrary,
  forceReaddAllDecks,
} from "@/lib/db";
import { inspectQueryError, validateWriteReadConsistency, shouldValidateReadAfterWrite } from "@/diagnostics";
import { getLocalizedTestData } from "@/lib/testData";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";

const centerStyle: React.CSSProperties = {
  minHeight: "100dvh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  background: "hsl(var(--bg))",
  fontFamily: "DM Sans, sans-serif",
  textAlign: "center",
  padding: 24,
};

function LogoWithFallback({ className }: { className?: string }) {
  const [imgError, setImgError] = useState(false);
  if (imgError) {
    return (
      <div className="flex items-center gap-2">
        <BookOpen className="h-8 w-8 shrink-0" style={{ color: "hsl(var(--primary))" }} />
        <span className="text-lg font-bold" style={{ color: "hsl(var(--foreground))" }}>StudyDo</span>
      </div>
    );
  }
  return (
    <img src="/logo-transparent.png" alt="StudyDo Logo" className={className} onError={() => setImgError(true)} />
  );
}

function StudyDoLoadingMark() {
  return (
    <div className="studydo-loading-mark" aria-hidden="true">
      <div className="studydo-loading-mark__halo" />
      <LogoWithFallback className="studydo-loading-mark__logo" />
      <div className="studydo-loading-mark__bars">
        <span className="studydo-loading-mark__bar studydo-loading-mark__bar--left" />
        <span className="studydo-loading-mark__bar studydo-loading-mark__bar--center" />
        <span className="studydo-loading-mark__bar studydo-loading-mark__bar--right" />
      </div>
    </div>
  );
}

export default function TeacherLibrary() {
  const { t, i18n } = useTranslation();
  const { teacherId } = useParams<{ teacherId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const publicHomeHref = user ? "/" : "/landing";
  const [decks, setDecks] = useState<Deck[]>([]);
  const [totalDecks, setTotalDecks] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [savingDeckId, setSavingDeckId] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [typeSubmenuOpen, setTypeSubmenuOpen] = useState(false);
  const [activeTypeFilter, setActiveTypeFilter] = useState<StudyModeKey | null>(null);
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  const loadMoreObserverRef = useRef<IntersectionObserver | null>(null);
  const requestVersionRef = useRef(0);
  const loadingRef = useRef(true);
  const loadingMoreRef = useRef(false);
  const decksRef = useRef<Deck[]>([]);
  const totalDecksRef = useRef(0);
  const PAGE_SIZE = 60;
  const supportsInfiniteScroll =
    typeof window === "undefined" || typeof window.IntersectionObserver !== "undefined";

  const lang = (i18n.language || "en").split("-")[0];
  const hasMoreDecks = decks.length < totalDecks;

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    loadingMoreRef.current = loadingMore;
  }, [loadingMore]);

  useEffect(() => {
    decksRef.current = decks;
  }, [decks]);

  useEffect(() => {
    totalDecksRef.current = totalDecks;
  }, [totalDecks]);

  useEffect(() => {
    if (!filterDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target as Node)) {
        setFilterDropdownOpen(false);
        setTypeSubmenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [filterDropdownOpen]);

  const groupedDecks = useMemo(() => {
    const groups: Record<TimeGroup, Deck[]> = { today: [], this_week: [], older: [] };
    for (const deck of decks) {
      const g = getTimeGroup(deck.created_at);
      groups[g].push(deck);
    }
    return groups;
  }, [decks]);

  const getFilterButtonLabel = (): string => {
    if (activeTypeFilter) {
      const keys: Record<StudyModeKey, string> = {
        cards: t("deckModal.studyModeCards"),
        learn: t("deckModal.studyModeLearn"),
        test: t("deckModal.studyModeTest"),
        match: t("deckModal.studyModeMatch"),
      };
      return keys[activeTypeFilter];
    }
    return t("home.sortRecent");
  };
  const deckHasTestData = (deck: Deck) =>
    deck.cards?.length
      ? deck.cards.some((c) => getLocalizedTestData(c, lang))
      : (deck.testableCount ?? 0) > 0;

  const loadMore = useCallback(async () => {
    if (!teacherId || loadingRef.current || loadingMoreRef.current || decksRef.current.length >= totalDecksRef.current) {
      return;
    }

    loadingMoreRef.current = true;
    setLoadingMore(true);
    const requestVersion = requestVersionRef.current;

    try {
      const { items, total } = await fetchTeacherLibraryPage({
        teacherId,
        limit: PAGE_SIZE,
        offset: decksRef.current.length,
        typeFilter: activeTypeFilter ?? undefined,
      });

      if (requestVersion !== requestVersionRef.current) {
        return;
      }

      if (items.length === 0) {
        const loadedCount = decksRef.current.length;
        totalDecksRef.current = loadedCount;
        setTotalDecks(loadedCount);
        return;
      }

      setDecks((prev) => {
        const merged = mergeUniqueDecks(prev, items);
        decksRef.current = merged;
        return merged;
      });
      totalDecksRef.current = total;
      setTotalDecks(total);
    } catch (error) {
      console.error("Failed to load more teacher library decks:", error);
    } finally {
      if (requestVersion === requestVersionRef.current) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [teacherId, activeTypeFilter]);

  useEffect(() => {
    requestVersionRef.current += 1;
    const requestVersion = requestVersionRef.current;

    if (!teacherId) {
      decksRef.current = [];
      totalDecksRef.current = 0;
      setDecks([]);
      setTotalDecks(0);
      setNotFound(true);
      setLoading(false);
      loadingRef.current = false;
      return;
    }

    loadingRef.current = true;
    loadingMoreRef.current = false;
    decksRef.current = [];
    totalDecksRef.current = 0;
    setLoading(true);
    setLoadingMore(false);
    setDecks([]);
    setTotalDecks(0);

    fetchTeacherLibraryPage({
      teacherId,
      limit: PAGE_SIZE,
      offset: 0,
      typeFilter: activeTypeFilter ?? undefined,
    })
      .then(({ items, total }) => {
        if (requestVersion !== requestVersionRef.current) return;
        decksRef.current = items;
        totalDecksRef.current = total;
        setDecks(items);
        setTotalDecks(total);
        setNotFound(false);
      })
      .catch((error) => {
        if (requestVersion !== requestVersionRef.current) return;
        inspectQueryError({
          queryName: "fetchTeacherLibraryPage",
          flow: "teacherLibrary.load",
          view: "deck_summaries",
          filters: { teacherId },
          responseError: error,
        });
        console.error("Failed to load teacher library page:", error);
        setNotFound(true);
      })
      .finally(() => {
        if (requestVersion !== requestVersionRef.current) return;
        loadingRef.current = false;
        setLoading(false);
      });
  }, [teacherId, activeTypeFilter]);

  useEffect(() => {
    if (!supportsInfiniteScroll || loading || loadingMore || !hasMoreDecks || !loadMoreSentinelRef.current) {
      loadMoreObserverRef.current?.disconnect();
      loadMoreObserverRef.current = null;
      return;
    }

    const sentinel = loadMoreSentinelRef.current;
    let requested = false;

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];
        if (!firstEntry?.isIntersecting || requested) return;
        requested = true;
        void loadMore();
      },
      {
        root: null,
        rootMargin: "0px 0px 320px 0px",
        threshold: 0.01,
      }
    );

    observer.observe(sentinel);
    loadMoreObserverRef.current = observer;

    return () => {
      observer.disconnect();
      if (loadMoreObserverRef.current === observer) {
        loadMoreObserverRef.current = null;
      }
    };
  }, [supportsInfiniteScroll, loading, loadingMore, hasMoreDecks, loadMore]);

  const showSyncResult = (decksAdded: number, cardsAdded: number) => {
    if (decksAdded === 0 && cardsAdded === 0) {
      toast.success(t("library.syncSuccessNothing"));
    } else if (decksAdded > 0 && cardsAdded > 0) {
      toast.success(
        t("library.syncSuccess", { decks: decksAdded, cards: cardsAdded })
      );
    } else if (decksAdded > 0) {
      toast.success(t("library.syncSuccessDecksOnly", { count: decksAdded }));
    } else {
      toast.success(t("library.syncSuccessCardsOnly", { count: cardsAdded }));
    }
  };

  const handleSmartSync = async () => {
    if (!user || !teacherId) {
      navigate("/auth", { state: { returnTo: `/library/${teacherId}` } });
      return;
    }
    setSyncing(true);
    try {
      const result = await smartSyncLibrary(teacherId, user.id);
      showSyncResult(result.decksAdded, result.cardsAdded);
      if (result.decksAdded > 0 || result.cardsAdded > 0) {
        navigate("/", { replace: true });
      }
    } catch (err) {
      console.error("Smart sync failed:", err);
      toast.error(t("library.syncError"));
    } finally {
      setSyncing(false);
    }
  };

  const handleForceReaddAll = async () => {
    if (!user || !teacherId) return;
    setSyncing(true);
    try {
      const result = await forceReaddAllDecks(teacherId, user.id);
      showSyncResult(result.decksAdded, result.cardsAdded);
      navigate("/", { replace: true });
    } catch (err) {
      console.error("Force re-add failed:", err);
      toast.error(t("library.syncError"));
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveToAccount = async (deck: Deck) => {
    if (!user) {
      navigate("/auth", { state: { returnTo: `/library/${teacherId}` } });
      return;
    }
    setSavingDeckId(deck.id);
    try {
      let deckToSave = deck;
      if (!deck.cards?.length) {
        const full = await fetchGlobalDeckForStudy(deck.id);
        if (full) deckToSave = full;
      }
      const created = await createDeck(
        user.id,
        {
          title: deckToSave.title,
          lang: deckToSave.lang,
          emoji: deckToSave.emoji,
          level: deckToSave.level,
          deckType: deckToSave.deckType,
          studyModes: deckToSave.studyModes ?? { cards: true, learn: true, test: true, match: true },
          cards: deckToSave.cards,
        },
        { sourceDeckId: deck.id }
      );
      if (created.cards?.length) {
        triggerBackgroundTtsForCards(created.cards, created.lang ?? "en");
      }
      if (shouldValidateReadAfterWrite("library.addDeckToLibrary")) {
        validateWriteReadConsistency({
          flow: "library.addDeckToLibrary",
          entity: "deck",
          entityId: created.id,
          userId: user.id,
          writeSucceeded: true,
          expectedPresence: true,
          fetchValidator: () => fetchDecksLight(user.id),
          checkPresence: (list) => Array.isArray(list) && list.some((d) => d.id === created.id),
        }).catch(() => {});
      }
      navigate(`/library/${teacherId}`, { replace: true });
    } catch (err) {
      if (err instanceof DeckAlreadyImportedError) {
        toast.info(t("shared.deckAlreadyInLibrary"));
      } else {
        inspectQueryError({
          queryName: "createDeck",
          flow: "teacherLibrary.addToAccount",
          table: "decks",
          responseError: err,
        });
        console.error("Failed to save deck:", err);
      }
      setSavingDeckId(null);
    }
  };

  if (loading) {
    return (
      <div style={centerStyle}>
        <div className="mb-5">
          <StudyDoLoadingMark />
        </div>
        <div
          style={{
            color: "hsl(var(--muted-foreground))",
            fontFamily: "DM Sans, sans-serif",
          }}
        >
          {t("library.loading")}
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={centerStyle}>
        <div className="flex justify-center mb-4"><Lock size={48} strokeWidth={1.5} style={{ color: "hsl(var(--muted-foreground))" }} /></div>
        <div
          style={{
            fontSize: 20,
            fontWeight: 600,
            marginBottom: 8,
            fontFamily: "DM Sans, sans-serif",
          }}
        >
          {t("library.notFound")}
        </div>
        <div
          style={{
            color: "hsl(var(--muted-foreground))",
            fontFamily: "DM Sans, sans-serif",
            marginBottom: 24,
          }}
        >
          {t("library.notFoundHint")}
        </div>
        <Link
          to={publicHomeHref}
          style={{
            padding: "10px 24px",
            borderRadius: 50,
            background: "hsl(var(--primary))",
            color: "white",
            textDecoration: "none",
            fontFamily: "DM Sans, sans-serif",
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          {t("library.goHome")}
        </Link>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col overflow-x-hidden w-full max-w-full"
      style={{
        background: "hsl(var(--bg))",
        fontFamily: "DM Sans, sans-serif",
      }}
    >
      {/* Header */}
      <div className="border-b border-[hsl(var(--border))] bg-[hsl(var(--bg2))]">
        <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-3 px-4 py-3 sm:px-6 md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 items-center">
            <div className="flex min-w-0 flex-col">
              <LogoWithFallback className="block h-9 w-auto max-w-[150px] object-contain sm:h-10 sm:max-w-[170px]" />
              <div className="mt-1 text-[10px] italic text-[hsl(var(--muted-foreground))] sm:text-[11px]">
                {t("library.heading")}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 md:justify-end">
            <div className="flex items-center gap-2">
              <LanguageSwitcher compact />
              <ThemeToggle compact />
            </div>
            {user && (
              <Link
                to={publicHomeHref}
                className="inline-flex h-10 items-center justify-center rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] px-4 text-[13px] font-semibold text-[hsl(var(--foreground))] no-underline transition-colors hover:bg-[hsl(var(--bg3))]/80"
                style={{ fontFamily: "DM Sans, sans-serif" }}
              >
                {t("shared.goToLinguaDeck")}
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div
        className="w-full max-w-full min-w-0 overflow-hidden box-border"
        style={{
          flex: 1,
          padding: "32px 24px 48px",
          maxWidth: 1200,
          margin: "0 auto",
        }}
      >
        <header className="mb-6 flex flex-col space-y-3 sm:space-y-4 md:flex-row md:items-end md:justify-between md:gap-6">
          <div className="min-w-0 space-y-1.5">
            <h1
              style={{
                fontFamily: "Instrument Serif, serif",
                fontSize: 28,
                margin: 0,
                color: "hsl(var(--foreground))",
              }}
            >
              {t("library.heading")}
            </h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] text-neutral-500 dark:text-neutral-400">
              {t("library.deckCount", { count: totalDecks })}
            </p>
          </div>

          <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-end sm:justify-end sm:gap-3 md:w-auto">
            {decks.length > 0 && (
              <div ref={filterDropdownRef} className="relative flex w-full flex-col gap-1 sm:w-auto">
                <span className="px-1 text-[11px] font-semibold text-[hsl(var(--muted-foreground))]">
                  {t("home.filter")}
                </span>
                <button
                  type="button"
                  onClick={() => setFilterDropdownOpen((o) => !o)}
                  className="inline-flex h-11 w-full items-center justify-between gap-2 rounded-full bg-neutral-100 dark:bg-[hsl(var(--bg3))] px-4 text-left text-[13px] font-semibold text-[hsl(var(--foreground))] transition-colors duration-200 hover:bg-neutral-200 dark:hover:bg-[hsl(var(--bg3))]/80 sm:min-w-[190px]"
                  style={{ fontFamily: "DM Sans, sans-serif" }}
                >
                  {getFilterButtonLabel()}
                  <ChevronDown size={16} className={`shrink-0 transition-transform duration-200 ${filterDropdownOpen ? "rotate-180" : ""}`} />
                </button>
                {filterDropdownOpen && (
                  <div
                    className="absolute right-0 top-full z-50 mt-2 min-w-0 w-full max-w-full sm:w-[220px]"
                    style={{
                      padding: 8,
                      background: "hsl(var(--card-bg))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "var(--radius)",
                      boxShadow: "0 4px 12px rgba(0,0,0,.15)",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setActiveTypeFilter(null);
                        setFilterDropdownOpen(false);
                        setTypeSubmenuOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2.5 rounded-full text-[13px] font-medium transition-colors duration-200 ${activeTypeFilter === null ? "bg-[hsl(var(--primary))] text-white" : "hover:bg-neutral-100 dark:hover:bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))]"}`}
                      style={{ fontFamily: "DM Sans, sans-serif", cursor: "pointer" }}
                    >
                      {t("home.sortRecent")}
                    </button>
                    <div>
                      <button
                        type="button"
                        onClick={() => setTypeSubmenuOpen((o) => !o)}
                        className={`w-full text-left px-3 py-2.5 rounded-full text-[13px] font-medium transition-colors duration-200 flex items-center justify-between ${activeTypeFilter ? "bg-[hsl(var(--primary))] text-white" : "hover:bg-neutral-100 dark:hover:bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))]"}`}
                        style={{ fontFamily: "DM Sans, sans-serif", cursor: "pointer" }}
                      >
                        {t("home.sortByType")}
                        <ChevronRight size={14} className={`shrink-0 transition-transform duration-200 ${typeSubmenuOpen ? "rotate-90" : ""}`} />
                      </button>
                      {typeSubmenuOpen && (
                        <div style={{ paddingLeft: 12, marginTop: 4 }}>
                          {(["cards", "learn", "test", "match"] as const).map((mode) => (
                            <button
                              key={mode}
                              type="button"
                              onClick={() => {
                                setActiveTypeFilter(mode);
                                setFilterDropdownOpen(false);
                                setTypeSubmenuOpen(false);
                              }}
                              className={`w-full text-left px-3 py-2 rounded-full text-[13px] font-medium transition-colors duration-200 ${activeTypeFilter === mode ? "bg-[hsl(var(--primary))] text-white" : "hover:bg-neutral-100 dark:hover:bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))]"}`}
                              style={{ fontFamily: "DM Sans, sans-serif", cursor: "pointer" }}
                            >
                              {t(`deckModal.studyMode${mode.charAt(0).toUpperCase() + mode.slice(1)}`)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {user && decks.length > 0 && (
              <div className="flex w-full items-end gap-2 sm:w-auto">
                <button
                  onClick={handleSmartSync}
                  disabled={syncing}
                  className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-full px-5 text-sm font-semibold text-white shadow-sm transition-all duration-150 hover:brightness-110 disabled:cursor-wait disabled:opacity-80 sm:flex-none"
                  style={{
                    background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--accent2)))",
                    fontFamily: "DM Sans, sans-serif",
                    boxShadow: "0 4px 12px rgba(109,90,255,.25)",
                  }}
                >
                  {syncing ? (
                    <>
                      <span
                        style={{
                          width: 16,
                          height: 16,
                          border: "2px solid rgba(255,255,255,.3)",
                          borderTopColor: "white",
                          borderRadius: "50%",
                          animation: "spin 0.8s linear infinite",
                        }}
                      />
                      {t("library.syncing")}
                    </>
                  ) : (
                    <><RefreshCw size={14} /> {t("library.syncLibrarySmart")}</>
                  )}
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      disabled={syncing}
                      className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--muted-foreground))] transition-colors hover:bg-[hsl(var(--bg3))]/80 disabled:cursor-wait disabled:opacity-60"
                      aria-label={t("library.forceReaddAll")}
                    >
                      <ChevronDown size={18} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="min-w-0 max-w-[100vw] w-full"
                    style={{
                      background: "hsl(var(--card-bg))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "var(--radius)",
                      padding: 4,
                    }}
                  >
                    <DropdownMenuItem
                      onClick={handleForceReaddAll}
                      disabled={syncing}
                      className="text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
                      style={{
                        fontSize: 13,
                        padding: "8px 12px",
                        cursor: syncing ? "wait" : "pointer",
                      }}
                    >
                      {t("library.forceReaddAll")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
        </header>

        {decks.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: 60,
              background: "hsl(var(--card-bg))",
              border: "2px dashed hsl(var(--border))",
              borderRadius: "var(--radius)",
              color: "hsl(var(--muted-foreground))",
            }}
          >
            <div className="flex justify-center mb-4"><Inbox size={48} strokeWidth={1.5} style={{ color: "hsl(var(--muted-foreground))" }} /></div>
            {t("library.empty")}
          </div>
        ) : (
          <div style={{ marginTop: 0 }}>
            {(["today", "this_week", "older"] as const).map((groupKey) => {
              const items = groupedDecks[groupKey];
              if (items.length === 0) return null;
              const labels: Record<TimeGroup, string> = {
                today: t("home.groupToday"),
                this_week: t("home.groupThisWeek"),
                older: t("home.groupOlder"),
              };
              return (
                <section key={groupKey} style={{ marginBottom: 32 }}>
                  <h3
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                      color: "hsl(var(--muted-foreground))",
                      marginBottom: 12,
                      marginTop: groupKey === "today" ? 0 : 24,
                    }}
                  >
                    {labels[groupKey]}
                  </h3>
                  <div
                    className="grid gap-4 w-full max-w-full min-w-0"
                    style={{
                      gridTemplateColumns: "repeat(auto-fill, minmax(min(260px, 100%), 1fr))",
                    }}
                  >
                    {items.map((deck) => (
                      <div
                        key={deck.id}
                        className="home-deck-card border border-[hsl(var(--border))] hover:border-neutral-300 dark:hover:border-neutral-600 p-4 md:p-6"
                        style={{
                          background: "hsl(var(--card-bg))",
                          borderRadius: "var(--radius)",
                          position: "relative",
                          overflow: "hidden",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 3,
                            background:
                              "linear-gradient(90deg, hsl(var(--primary)), hsl(var(--accent2)))",
                          }}
                        />
                        <div className="inline-flex items-center gap-1.5 self-start rounded-lg bg-[hsl(var(--bg3))] px-2.5 py-0.5 md:py-1 text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-2 md:mb-3">
                          <Globe size={12} />
                          {deck.lang.toUpperCase()}
                          {deck.level && ` · ${deck.level}`}
                        </div>
                        <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
                          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-[hsl(var(--primary)/.12)] text-[hsl(var(--primary))] shrink-0">
                            <BookOpen size={20} />
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div className="text-[15px] font-semibold text-[hsl(var(--foreground))] leading-tight truncate">
                              {deck.title}
                            </div>
                            <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                              {t("sidebar.cardsCount", { count: deck.cardCount ?? deck.cards?.length ?? 0 })}
                            </div>
                          </div>
                        </div>

                        <div className="flex flex-col gap-2 md:gap-3 mt-auto">
                          <div className="grid gap-2 md:gap-3" style={{ gridTemplateColumns: deckHasTestData(deck) ? "1fr 1fr" : "1fr" }}>
                            <button
                              onClick={() =>
                                navigate(`/library/${teacherId}/learn/${deck.id}`)
                              }
                              className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-2xl border-0 bg-[hsl(var(--primary))] text-white text-sm font-semibold shadow-sm cursor-pointer hover:shadow-md hover:brightness-110 transition-all duration-200 ease-out hover:scale-[1.02] active:scale-[0.98]"
                            >
                              <Play size={14} fill="currentColor" /> {t("library.learn")}
                            </button>
                            {deckHasTestData(deck) && (
                              <button
                                onClick={() =>
                                  navigate(`/library/${teacherId}/test/${deck.id}`)
                                }
                                className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] text-sm font-semibold cursor-pointer hover:shadow-md hover:bg-[hsl(var(--bg3))]/80 transition-all duration-150 active:scale-[0.98]"
                              >
                                <ClipboardList size={14} /> {t("library.test")}
                              </button>
                            )}
                          </div>
                          <button
                            onClick={() => handleSaveToAccount(deck)}
                            disabled={savingDeckId === deck.id}
                            className="inline-flex items-center justify-center gap-2 h-10 w-full px-4 rounded-2xl border border-emerald-300 bg-emerald-50 text-emerald-600 text-sm font-semibold cursor-pointer hover:bg-emerald-100 transition-all duration-150 active:scale-[0.98] disabled:opacity-60 disabled:cursor-wait dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-400 dark:hover:bg-emerald-500/20"
                          >
                            <Download size={14} />
                            {savingDeckId === deck.id
                              ? "…"
                              : t("library.saveToAccount")}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              );
            })}

            {hasMoreDecks && (
              <div className="mt-6 flex flex-col items-center gap-3">
                <div
                  ref={loadMoreSentinelRef}
                  aria-hidden="true"
                  className="h-1 w-full"
                />
                {loadingMore && (
                  <div className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] px-4 py-2 text-sm font-medium text-[hsl(var(--muted-foreground))]">
                    <span
                      aria-hidden="true"
                      className="h-4 w-4 rounded-full border-2 border-[hsl(var(--border))] border-t-[hsl(var(--primary))]"
                      style={{ animation: "spin 0.8s linear infinite" }}
                    />
                    {t("library.loading")}
                  </div>
                )}
                {!supportsInfiniteScroll && (
                  <button
                    type="button"
                    onClick={() => void loadMore()}
                    disabled={loadingMore}
                    className="inline-flex items-center justify-center rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--bg3))] px-5 py-2.5 text-sm font-semibold text-[hsl(var(--foreground))] transition-all duration-150 hover:bg-[hsl(var(--bg3))]/80 active:scale-[0.98] disabled:opacity-60 disabled:cursor-wait"
                  >
                    {loadingMore ? "…" : t("common.showMore")}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* CTA for non-logged-in users */}
        {!user && decks.length > 0 && (
          <div
            style={{
              marginTop: 40,
              padding: "24px 28px",
              background: "hsl(var(--card-bg))",
              border: "1px solid hsl(var(--border))",
              borderRadius: "var(--radius)",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                marginBottom: 6,
                color: "hsl(var(--foreground))",
              }}
            >
              {t("shared.wantToCreateDecks")}
            </div>
            <div
              style={{
                fontSize: 13,
                color: "hsl(var(--muted-foreground))",
                marginBottom: 14,
              }}
            >
              {t("shared.promoSignUp")}
            </div>
            <Link
              to="/auth"
              style={{
                display: "inline-block",
                padding: "10px 24px",
                borderRadius: 50,
                background: "hsl(var(--primary))",
                color: "white",
                textDecoration: "none",
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {t("shared.getStartedFree")}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
